import { Card, CardContent, Typography, Chip, Box, ImageList, ImageListItem, Dialog, DialogContent, IconButton } from "@mui/material"
import CloseIcon from "@mui/icons-material/Close"
import CameraAltIcon from "@mui/icons-material/CameraAlt"
import ArchiveIcon from "@mui/icons-material/Archive"
import React from "react"
import AddObservation from "../../../components/Observations/AddObservation"
import MarkerObservationChart from "../../../components/Map/MarkerObservationChart"
import { gql, useMutation } from "@apollo/client"
import dayjs from "dayjs"

const GET_IMAGE = gql`
  mutation getImage($file_key: String!) {
    fileDownload(file_key: $file_key) {
      base64EncodedFile
    }
  }
`

function ImageGalleryItem({ s3_key, onClick }) {
  const [getImage] = useMutation(GET_IMAGE)
  const [imageBase64, setImageBase64] = React.useState()
  const [loading, setLoading] = React.useState(true)

  React.useEffect(() => {
    let isMounted = true
    async function fetchImage() {
      setLoading(true)
      try {
        const { data } = await getImage({ variables: { file_key: s3_key } })
        if (isMounted) {
          setImageBase64(data.fileDownload.base64EncodedFile)
        }
      } finally {
        if (isMounted) setLoading(false)
      }
    }
    fetchImage()
    return () => { isMounted = false }
  }, [s3_key, getImage])

  if (loading) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" height="100%" minHeight={120}>
        <span className="gallery-loading-spinner" style={{ display: 'inline-block', width: 32, height: 32 }}>
          <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="16" cy="16" r="14" stroke="#888" strokeWidth="4" opacity="0.2" />
            <path d="M16 2a14 14 0 0 1 14 14" stroke="#1976d2" strokeWidth="4" strokeLinecap="round" />
          </svg>
        </span>
      </Box>
    )
  }

  if (!imageBase64) return null

  return (
    <img 
      src={`data:image/jpeg;base64,${imageBase64}`}
      alt="observation"
      style={{ 
        width: '100%', 
        height: '100%', 
        objectFit: 'cover',
        cursor: 'pointer'
      }}
      onClick={onClick}
      loading="lazy"
    />
  )
}

export default function ActiveMarker({ marker, onArchive }) {
  const [addingObservation, setAddingObservation] = React.useState(false)
  const [selectedImage, setSelectedImage] = React.useState(null)
  const [getImage] = useMutation(GET_IMAGE)
  const [fullSizeImage, setFullSizeImage] = React.useState(null)
  const [hoveredObservationDate, setHoveredObservationDate] = React.useState(null)
  const [selectedImageNotes, setSelectedImageNotes] = React.useState("")

  const observations = marker?.observations || []
  const observationsWithImages = observations?.filter(
    (ob) => ob.observation_attachments.length > 0
  )
  
  // Collect all images from all observations, keeping track of the observation date
  const allImages = observationsWithImages?.flatMap((ob) => {
    console.log('Observation data:', {
      id: ob.id,
      observation_date: ob.observation_date,
      date_added: ob.date_added,
      data_date: ob.data?.date,
      payload_version: ob.payload_version
    })
    // Use the same date logic as the chart: data?.date || observation_date
    const observationDate = ob.data?.date || ob.observation_date
    return ob.observation_attachments.map((attachment) => ({
      s3_key: attachment.file.key,
      date: observationDate,
      observationId: ob.id,
      notes: ob.data?.notes || "",
    }))
  }) || []

  const markerLabel = "Mark"
  const markerIcon = "📍"

  const handleImageClick = async (s3_key, notes) => {
    setSelectedImage(s3_key)
    setSelectedImageNotes(notes)
    const { data } = await getImage({ variables: { file_key: s3_key } })
    setFullSizeImage(data.fileDownload.base64EncodedFile)
  }

  const handleCloseDialog = () => {
    setSelectedImage(null)
    setFullSizeImage(null)
    setSelectedImageNotes("")
  }

  return (
    <>
      <AddObservation
        locationId={marker.id}
        observationVersion={2}
        open={addingObservation}
        setOpen={setAddingObservation}
      />
      
      <Card style={{ margin: '10px 0' }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" marginBottom={1}>
            <Typography variant="h6">
              {markerIcon} {marker.name || `${markerLabel} ${marker.id}`}
            </Typography>
            <Box display="flex" gap={1}>
              <IconButton
                color="primary"
                onClick={() => setAddingObservation(true)}
                title="Add Observation"
                size="small"
                variant="outlined"
              >
                <CameraAltIcon />
              </IconButton>
              <IconButton
                color="error"
                onClick={onArchive}
                title="Archive Mark"
                size="small"
                variant="outlined"
              >
                <ArchiveIcon />
              </IconButton>
            </Box>
          </Box>
          <Box display="flex" gap={1} flexWrap="wrap">
            {marker.metadata?.species && (
              <Chip label={`Species: ${marker.metadata.species}`} size="small" color="secondary" />
            )}
            {marker.metadata?.deployment_date && (
              <Chip label={`Deployed: ${marker.metadata.deployment_date}`} size="small" />
            )}
            {marker.metadata?.purpose && (
              <Chip label={`Purpose: ${marker.metadata.purpose}`} size="small" color="info" />
            )}
          </Box>
        </CardContent>
      </Card>
      
      {observations.length > 0 && (
        <MarkerObservationChart 
          observations={observations} 
          markerName={marker.name || `${markerLabel} ${marker.id}`}
          highlightedDate={hoveredObservationDate}
        />
      )}
      
      {allImages.length > 0 && (
        <Card style={{ margin: '10px 0' }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Photo Gallery
            </Typography>
            <ImageList cols={3} gap={8} sx={{ width: '100%', maxHeight: 500 }}>
              {allImages.map((image) => (
                <ImageListItem 
                  key={image.s3_key}
                  onMouseEnter={() => {
                    console.log('Hovering image with date:', image.date)
                    setHoveredObservationDate(image.date)
                  }}
                  onMouseLeave={() => setHoveredObservationDate(null)}
                  sx={{
                    border: hoveredObservationDate === image.date ? '3px solid #ff4444' : '3px solid transparent',
                    borderRadius: '4px',
                    overflow: 'hidden',
                    transition: 'all 0.2s ease',
                    boxShadow: hoveredObservationDate === image.date ? '0 0 12px rgba(255, 68, 68, 0.6)' : 'none',
                  }}
                >
                  <ImageGalleryItem 
                    s3_key={image.s3_key} 
                    onClick={() => handleImageClick(image.s3_key, image.notes)}
                  />
                  <Box
                    sx={{
                      position: 'absolute',
                      bottom: 0,
                      left: 0,
                      right: 0,
                      background: 'rgba(0, 0, 0, 0.6)',
                      color: 'white',
                      padding: '4px 8px',
                      fontSize: '11px',
                      textAlign: 'center',
                    }}
                  >
                    {dayjs(image.date).format("MMM DD, HH:mm")}
                  </Box>
                  {image.notes && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 4,
                        right: 4,
                        background: 'rgba(255, 255, 255, 0.9)',
                        borderRadius: '50%',
                        width: '24px',
                        height: '24px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '14px',
                      }}
                      title={image.notes}
                    >
                      📝
                    </Box>
                  )}
                </ImageListItem>
              ))}
            </ImageList>
          </CardContent>
        </Card>
      )}

      <Dialog 
        open={selectedImage !== null} 
        onClose={handleCloseDialog}
        maxWidth="lg"
        fullWidth
      >
        <IconButton
          onClick={handleCloseDialog}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: 'white',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.7)',
            },
          }}
        >
          <CloseIcon />
        </IconButton>
        <DialogContent sx={{ padding: 0, backgroundColor: 'black' }}>
          {fullSizeImage && (
            <>
              <img 
                src={`data:image/jpeg;base64,${fullSizeImage}`}
                alt="observation full size"
                style={{ 
                  width: '100%', 
                  height: 'auto',
                  display: 'block'
                }}
              />
              {selectedImageNotes && (
                <Box
                  sx={{
                    padding: '16px',
                    backgroundColor: 'rgba(255, 255, 255, 0.95)',
                    color: '#333',
                  }}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', marginBottom: '8px' }}>
                    Notes:
                  </Typography>
                  <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                    {selectedImageNotes}
                  </Typography>
                </Box>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  )
}
